<template>
  <div
    class="ai-speed-slider js-ai-speed"
    :class="{ '-popup-open': showPopup }"
  >
    <!-- Placeholder that shows current speed -->
    <div
      class="-placeholder"
      @click="openPopup"
      :class="{ '-disabled': disabled, '-popup-open': showPopup }"
    >
      <span class="wticon wticon-speed" />
      <span class="-label">{{ currentSpeedLabel }}</span>
      <i class="-icon -chevron wticon wticon-chevron-down" />
    </div>

    <!-- Popup with slider -->
    <div v-if="showPopup" class="-popup">
      <div class="-header">Select voice speed</div>
      <div class="-slider-container">
        <wt-slider
          v-model="internalSpeed"
          :min="0.7"
          :max="1.2"
          :sliderStep="0.01"
          :steps="speedSteps"
          :showStepsMarker="false"
          :showInputBox="false"
          :disabled="disabled"
          @change="onSpeedChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import WtSliderComponent from 'components/sliders/wtSlider.component.vue';

export default {
  name: 'AiSpeedSlider',
  components: {
    wtSlider: WtSliderComponent,
  },
  props: {
    currentSpeed: {
      type: Number,
      default: 1.0,
    },
    speedOptions: {
      type: Array,
      default: () => [
        { value: 0.7, label: '0.7x (Slower)' },
        { value: 0.8, label: '0.8x (Slower)' },
        { value: 0.9, label: '0.9x (Slower)' },
        { value: 1.0, label: '1.0x (Normal)' },
        { value: 1.1, label: '1.1x (Faster)' },
        { value: 1.2, label: '1.2x (Faster)' },
      ],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPopup: false,
      internalSpeed: this.currentSpeed,
    };
  },
  computed: {
    currentSpeedLabel() {
      const speed = this.currentSpeed;
      let label = '';

      if (speed === 1.0) {
        label = 'Normal';
      } else if (speed > 1.1) {
        label = 'Fast';
      } else if (speed > 1.0) {
        label = 'Faster';
      } else if (speed < 0.8) {
        label = 'Slow';
      } else if (speed < 1.0) {
        label = 'Slower';
      } else {
        label = 'Normal';
      }

      return `${speed}x (${label})`;
    },
    speedSteps() {
      // Exclude min and max values since wtSlider adds them automatically
      return this.speedOptions
        .map((option) => option.value)
        .filter((value) => value !== 0.7 && value !== 1.2);
    },
  },
  watch: {
    currentSpeed(newValue) {
      this.internalSpeed = newValue;
    },
    internalSpeed(newValue) {
      this.$emit('change', newValue);
    },
  },
  mounted() {
    // Close popup when clicking outside
    this.$nextTick(() => {
      document.addEventListener('click', this.handleOutsideClick);
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    openPopup() {
      if (!this.disabled) {
        this.showPopup = true;
        this.$emit('opened');
      }
    },
    closePopup() {
      this.showPopup = false;
      this.$emit('closed');
    },
    onSpeedChange(speed) {
      // Speed change is already handled by the watcher
      // This method can be used for additional logic if needed
    },
    handleOutsideClick(event) {
      if (this.showPopup) {
        // Check if the click is inside our component OR has a parent with js-ai-speed class
        const isInsideComponent = this.$el.contains(event.target);
        const hasAiSpeedParent = event.target.closest('.js-ai-speed');

        if (!isInsideComponent && !hasAiSpeedParent) {
          this.closePopup();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ai-speed-slider {
  position: relative;
  display: inline-block;

  .-placeholder {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(.-disabled),
    &.-popup-open {
      background-color: rgba(0, 0, 0, 0.05);
    }

    &:active:not(.-disabled),
    &.-popup-open {
      background-color: rgba(0, 0, 0, 0.05);
    }

    &.-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .wticon-speed {
    margin-right: 8px;
    color: #666;
  }

  .-label {
    flex: 1;
    font-size: 14px;
    color: #333;
  }

  .-icon.-chevron {
    margin-left: 8px;
    color: #000;
    font-size: 12px;
  }

  &.-popup-open .-icon.-chevron {
    transform: rotate(180deg);
  }

  .-popup {
    position: absolute;
    bottom: 100%;
    left: 0;
    width: 300px;
    margin-bottom: 4px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    padding: 16px;
    transform: translateX(-25%);

    .-header {
      color: rgba(0, 0, 0, 0.3);
      font-weight: 400;
      font-size: 14px;
      padding-bottom: 0px;
    }

    .-slider-container {
      width: 100%;
    }
  }

  /deep/ .wt2-slider {
    margin: 0;
    padding-bottom: 0;
  }
}
</style>
